export const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';
const TOKEN_KEY = 'access_token';
const USER_KEY = 'visitas_user';

export const getToken = () => localStorage.getItem(TOKEN_KEY) || '';

export const getStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const setSession = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

export const SESSION_EXPIRED_MESSAGE = 'Tu sesión expiró o no está autenticada. Ingresa nuevamente.';

const AUTH_ERROR_DETAILS = new Set([
  'Not authenticated',
  'Could not validate credentials',
  'No se pudieron validar las credenciales.',
]);

export const isAuthError = (response, data = null) => (
  response?.status === 401 && (!data?.detail || AUTH_ERROR_DETAILS.has(data.detail))
);

export const formatApiError = (response, data = null, fallback = 'No se pudo completar la solicitud.') => {
  if (isAuthError(response, data)) {
    return SESSION_EXPIRED_MESSAGE;
  }

  return data?.detail || data?.message || fallback;
};

export const notifySessionExpired = () => {
  clearSession();
  window.dispatchEvent(new Event('visitas:session-expired'));
};

const parseError = async (response) => {
  const data = await response.json().catch(() => null);
  if (isAuthError(response, data)) {
    notifySessionExpired();
  }
  return formatApiError(response, data);
};

const request = async (path, options = {}) => {
  const headers = new Headers(options.headers || {});
  const isFormData = options.body instanceof FormData;

  if (!isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const token = getToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
};

export const api = {
  get: (path) => request(path),
  post: (path, body) => request(path, {
    method: 'POST',
    body: body instanceof FormData ? body : JSON.stringify(body),
  }),
  put: (path, body) => request(path, {
    method: 'PUT',
    body: JSON.stringify(body),
  }),
  delete: (path) => request(path, { method: 'DELETE' }),
};

export const login = async (email, password, totpCode = '') => {
  const body = new URLSearchParams();
  body.set('username', email);
  body.set('password', password);
  if (totpCode) {
    body.set('totp_code', totpCode);
  }

  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!response.ok) {
    throw new Error(await parseError(response));
  }

  const data = await response.json();
  setSession(data.access_token, data.usuario);
  return data;
};

export const fetchMe = async () => api.get('/auth/me');

export const resetPasswordWithMfa = async (email, totpCode, newPassword) => (
  api.post('/auth/reset-password-mfa', {
    email,
    totp_code: totpCode,
    new_password: newPassword,
  })
);

export const changePassword = async (currentPassword, newPassword) => (
  api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  })
);

export const setupTotp = async () => api.post('/auth/totp/setup', {});

export const confirmTotp = async (code) => api.post('/auth/totp/confirm', { code });
